/**
 * The garden's locations (beds): listing, making, changing, archiving and
 * deleting them, and acting on the ticked plants in one.
 *
 * A location is sent with its own photos and notes and a summary of its
 * plants, so a bed's card can be drawn from one answer.
 *
 * Endpoints:
 * - GET    /locations[?status=&currentDate=&lang=] → Location[] (a bare list;
 *   each plant's names, and the bed's own line and About lines (D56), in the
 *   app's language, services/grow/plantNaming, services/beds/bedWords). The
 *   beds in use (status=1, W24 Job H) carry what the Beds card draws: their
 *   growing plants only and plant_count, archived_plants (the removed ones,
 *   which keep a bed from being deleted), `lately` (the season diary's newest
 *   rows for the bed) and `sharing` (the Share count and the Public switch;
 *   null when it could not be read);
 *   the archived ones (status=0) keep every plant a bed ever grew.
 * - GET    /locations?status=1&q=text → Location[]: the Beds search, answered
 *   on the server (a bed's name, description or its plants' names, removed
 *   ones included); the
 *   same objects the unsearched list carries (searchBeds).
 * - GET    /locations?short=1 → BedName[]: the beds in use as { id, name },
 *   for a drop-down (getBedNames, useBedNames).
 * - POST   /locations (multipart: the fields, and an optional 'photo') →
 *   LocationMutationResponse.
 * - PUT    /locations/:id (multipart, the same) → LocationMutationResponse.
 * - PATCH  /locations/:id/status { status } → { success, message }; refused
 *   while plants still grow in it.
 * - DELETE /locations/:id → LocationDeleteResponse.
 * - POST   /locations/:id/plants/status { plantObjIds, status } → { plantObjIds }.
 * - POST   /locations/:id/notes { content, plantObjIds } → { notes }.
 * - POST   /locations/:id/notes/undo { noteIds }.
 * - GET    /locations/bed-questions[?lang=] → BedQuestions: what Add bed and
 *   Edit bed ask for each kind of bed (D55; the server's table, the app draws
 *   it), the sources "What blocks the sun?" offers (shade, D57), and the names
 *   of the choices in the app's language (words; the same words the bed card
 *   uses).
 * A location's own journal and photos are api/notes and api/photos (the
 * *For functions, under /locations/:id/journal and /photos).
 *
 * Who uses it: shared/context/LocationsContext (the garden's beds for every
 * screen) and features/locations, packets, photo-tray, plant-list, today
 * and welcome. Caching: [LOCATIONS_QUERY_KEY] ('locations') for the beds in
 * use ([LOCATIONS_QUERY_KEY, language], useBeds) and [ARCHIVED_LOCATIONS_QUERY_KEY, userId, language] for the archived
 * (the language is in the keys because the server words each bed's lines and plants' names in it; B72); the Beds
 * search keeps its answers under [LOCATIONS_QUERY_KEY, language, 'search', text] (features/locations useBedSearch) and the
 * short list under [LOCATIONS_QUERY_KEY, 'names', language] (['locations', 'names', language], useBedNames), so
 * every change to a bed reads them again. A change to
 * a bed invalidates 'locations', and, when plants moved, 'plants' and
 * 'plant-outlook' too. Auth: the signed-in token,
 * in the garden being tended (X-Grow-Garden).
 *
 * @module api/locations
 */

import { apiClient } from './apiClient';
import type { PlantPhoto } from '../types/photo';
import type { PlantNames } from '../types/plant';
import type { Note } from './notes';
import type { SeasonDiaryRow } from './season';
import type { PlantSharing } from './shares';
import { getLanguage } from '@/shared/i18n/i18nSetup';

/** The kinds of bed (D55; the server's config/tunables/beds BED_KINDS). */
export type BedKind = 'ground' | 'raised' | 'pots_small' | 'pots_medium' | 'containers_large' | 'cold_frame' | 'greenhouse' | 'heated_greenhouse' | 'indoors' | 'seed_vault';

/** How much sun a bed gets. */
export type BedSun = 'full' | 'part_sun' | 'part_shade' | 'shade';

/** The covers a gardener can have on hand for a bed. */
export type BedCover = 'light_cloth' | 'heavy_cloth' | 'plastic_hoops' | 'sheets' | 'shade_30' | 'shade_50';

/** How a bed is watered. */
export type BedWatering = 'hand' | 'drip' | 'sprinkler' | 'timer';

/** How windy a bed's spot is. */
export type BedWind = 'sheltered' | 'exposed';

/**
 * What a bed is (D55; owner, 2026-10-16: "we need richer data about the bed
 * itself"), as the server keeps it: the to-do list acts on the bed with it.
 */
export interface BedFacts {
  /** Its kind: in the ground, raised, pots by size, a cold frame, a greenhouse, indoors. */
  kind: BedKind;
  /** How much sun it gets. */
  sun: BedSun;
  /** The covers on hand; [] for none; null when not said (the bed then gets the plain job, no cover check). */
  covers: BedCover[] | null;
  /** A cover stays on all season. */
  coverStays: boolean;
  /** Hoops are in place. */
  hoops: boolean;
  /** How it is watered, or null when not said. */
  watering: BedWatering | null;
  /** How windy its spot is, or null when not said. */
  wind: BedWind | null;
  /**
   * What blocks the sun (D57): a source's key from the server's bed_shade table ("trees", "building"), or null for
   * Not sure. The server keeps it only where the kind asks it and the bed is not in full sun.
   */
  shade: string | null;
}

/** What one kind of bed asks (the server's BED_QUESTIONS). */
export interface BedQuestion {
  /** Its sun is asked. */
  sun: boolean;
  /** The covers it can have; none, no covers question. */
  covers: BedCover[];
  /** Whether a cover stays on all season is asked. */
  coverStays: boolean;
  /** Hoops are asked. */
  hoops: boolean;
  /** Its watering is asked. */
  watering: boolean;
  /** Its wind is asked. */
  wind: boolean;
  /** "What blocks the sun?" is asked (D57), and then only when the bed is not in full sun; always sent (C118 F3). */
  shade: boolean;
}

/** The bed form's questions (GET /locations/bed-questions). */
export interface BedQuestions {
  /** Every kind, in the order the form lists them. */
  kinds: BedKind[];
  /** The sun answers, from the most. */
  sun: BedSun[];
  /** The watering answers. */
  watering: BedWatering[];
  /** The wind answers. */
  wind: BedWind[];
  /** What each kind asks. */
  questions: Record<BedKind, BedQuestion>;
  /** The kinds in groups as the form lists them (Outdoors, Pots, Under cover, Indoors). */
  kindGroups: Array<{ key: string; kinds: BedKind[] }>;
  /** The choices of "What's over it", in the order listed, with what each sets (the server's OVER_CHOICES). */
  over: BedOverChoice[];
  /** The covers asked as frost covers (or a greenhouse's inner layer). */
  frostCovers: BedCover[];
  /** The covers asked as shade cloth, one choice. */
  shadeCovers: BedCover[];
  /** The sun a bed needs for shade cloth to be asked. */
  shadeSun: BedSun[];
  /** The sources "What blocks the sun?" offers, in the server's order (bed_shade: trees, then building; D57). */
  shade: string[];
  /**
   * The names of the choices the form offers, in the reader's language, from the same words the bed card uses
   * (batch 11b, A68): each group by the choice's key, e.g. words.kind.raised is "Raised bed".
   */
  words: BedQuestionWords;
}

/** One choice of "What's over it": when it is offered, what choosing it sets, and when it is the one showing (the server decides). */
export interface BedOverChoice {
  /** The choice's key (nothing, hoops, season); the form's words are locations.facts.over.<key>. */
  key: 'nothing' | 'hoops' | 'season';
  /** The question the bed's kind must ask for this to be offered; null for always. */
  needs: 'hoops' | 'coverStays' | null;
  /** What choosing it sets: whether a cover stays, whether hoops are in place, and whether plastic on hoops is among the covers. */
  sets: { coverStays: boolean; hoops: boolean; plastic: boolean };
  /** The draft's state in which this is the choice showing (a cover that stays; hoops where none stays). */
  matches: { coverStays: boolean; hoops?: boolean };
}

/** The names of the bed form's choices, by group and then by the choice's key (the server's, in the reader's language). */
export interface BedQuestionWords {
  /** The kinds of bed: "Raised bed". */
  kind: Record<string, string>;
  /** How much sun: "Full sun". */
  sun: Record<string, string>;
  /** The covers: "Heavy frost cloth". */
  cover: Record<string, string>;
  /** How it is watered: "Soaker hose or drip". */
  watering: Record<string, string>;
  /** How windy: "Exposed to wind". */
  wind: Record<string, string>;
  /**
   * "What blocks the sun?" (D57, beds.form.shade.*): its question, each source's name by its key ("Trees", "A building
   * or fence") and Not sure's; always sent (C118 F3).
   */
  shade: { question: string; notSure: string } & Record<string, string>;
}

/** A stage entry as it appears on a location's plant. */
export interface LocationPlantStage {
  /** The stage's id. */
  stage_id?: number;
  /** Its name as the gardener sees it. */
  name?: string;
  /** The catalogue's type (SEEDING, MATURITY...), empty for a gardener's own stage. */
  stage_type?: string;
}

/**
 * A plant as it appears inside a location: a summary, not the full Plant.
 *
 * The server builds it from the same list the plant page reads, so a bed
 * lists exactly the plants the garden says are in it. It carries what a row
 * needs — the two names and the current stage — and nothing to draw a picture
 * with; opening the row fetches the whole plant.
 */
export interface LocationPlant {
  /** plant_obj.status: 1 growing, 0 archived. */
  status: number;
  /** The plant's name ("Tomato"). */
  plant_name: string;
  /** Its variety. */
  variety_id: number;
  /** Its variety's name as stored: never shown by itself; the rows print names. */
  variety_name: string;
  /** How every screen says it (services/grow/plantNaming), in the app's language. */
  names: PlantNames;
  /** The garden's plant. */
  plant_obj_id: number;
  /** The plant's current stage, as a one-item list (or empty). */
  current_stage: Array<{ stage: LocationPlantStage; plant_obj_id: number }>;
}

/** One line of About this bed (D56; the server's services/beds/bedWords). */
export interface BedAboutLine {
  /** "Kind", "Sun", "Covers"; null for a line said whole. */
  label: string | null;
  /** "Raised bed". */
  text: string;
}

/**
 * The one picture a bed's tile, row and card lead with, as the server picks it
 * (services/beds/bedCover; bug B2): the newest of its own photos, else the
 * cover the gardener chose, else Grow's default picture. Drawn as sent.
 */
export interface LocationCover {
  /** The photo it is (one of photos), or 0 for the bed's stored cover. */
  photo_id: number;
  /** Its full picture. */
  signed_photo_url: string | null;
  /** Its card-sized copy (the stored cover's one link). */
  cardUrl: string | null;
  /** Its thumbnail (the stored cover's one link). */
  thumbUrl: string | null;
  /** The safety check flagged it: the API client fills its links with "This photo is being reviewed" (shared/lib/underReview). */
  under_review?: boolean;
}

/** A location (bed) as the backend sends it. */
export interface Location {
  /** The location's id. */
  id: number;
  /** Its name ("Back bed"). */
  name: string;
  /** The gardener's description, if any. */
  description: string | null;
  /** The cover photo as stored (a filename, or an older absolute URL), or null; show signed_photo_url instead. */
  location_photo_url: string | null;
  /** A signed address for its stored cover (chosen or Grow's default), ready to show; the tiles draw cover. */
  signed_photo_url: string | null;
  /** The picture its tile, row and card draw (the server's pick); null for a bed with none. */
  cover: LocationCover | null;
  /** 1 in use, 0 archived. */
  status: number;
  /** True when the weather does not reach this location, so no warnings apply to its plants (D55: a cover that stays on, a cold frame, a greenhouse or indoors). */
  sheltered: boolean;
  /** What the bed is (D55). */
  bed: BedFacts;
  /** What the bed is in one line, in the app's language (D56): "Raised bed · full sun · heavy frost cloth on hoops". */
  bed_line: string;
  /** True when bed_line already says the bed is sheltered, so the card does not say it twice (the server decides). */
  bed_line_says_sheltered: boolean;
  /** About this bed's lines (D56): a label ("Kind") and its words, or no label for the last ("Watered by hand · exposed to wind"). */
  bed_about: BedAboutLine[];
  /** True while the location's public page is on. */
  is_public: boolean;
  /** D69: the garden's Seed vault (the server decides, from its kind's vault flag): the seed icon and the held seeds. */
  is_vault?: boolean;
  /** D69: on the vault, how many seeds wait for a tap; 0 elsewhere. */
  held?: number;
  /** D69: on the vault, today's mail in a line ("9 seeds from Territorial Seed Company went in today · 2 waiting"); null otherwise. */
  latest?: { text: string } | null;
  /** The location's own photos (newest first, as the server sends them), shaped like a plant's. */
  photos: PlantPhoto[];
  /** The location's own notes, shaped like a plant's. */
  notes: Note[];
  /**
   * The plants in it, as summaries: on the beds in use (status=1) only the ones growing now (W24 Job H); on an
   * archived bed (status=0) every plant it ever grew.
   */
  plants: LocationPlant[];
  /** How many plants grow in it now (the server's count; W24 Job H). */
  plant_count: number;
  /**
   * The removed plants that still name this bed (they keep it from being deleted), on the beds in use (status=1; W24
   * Job H); absent on an archived bed, whose `plants` lists them.
   */
  archived_plants?: Array<Pick<LocationPlant, 'plant_obj_id' | 'status' | 'names'>>;
  /**
   * What happened in it lately: the season diary's own newest rows for this bed, newest first, at most the Grow
   * setting season.BED_LATELY_COUNT; [] for a bed with none (the server's, W24 Job H).
   */
  lately: SeasonDiaryRow[];
  /**
   * Its public page's switch and numbers, exactly what GET /shares/location/:id answers (W24 Job H); null when the
   * server could not read them, and the card then reads the share route itself.
   */
  sharing: PlantSharing | null;
}

/** One bed in use as a drop-down lists it (GET /locations?short=1). */
export interface BedName {
  /** The bed's id. */
  id: number;
  /** Its name. */
  name: string;
}

/** What creating or updating a location answers. */
export interface LocationMutationResponse {
  /** The server's flag. */
  success: boolean;
  /** The server's sentence, in English. */
  message: string;
  /** The location as saved. */
  location: Location;
}

/** What deleting a location answers. */
export interface LocationDeleteResponse {
  /** The server's flag. */
  success: boolean;
  /** The server's sentence, in English. */
  message: string;
}

/**
 * Fetches locations from the backend, each plant named in the app's language (lang).
 * @param status Optional status filter.
 * @param currentDate Optional date filter (YYYY-MM-DD).
 * @returns A promise that resolves to an array of locations.
 */
export async function getLocations(status?: number, currentDate?: string): Promise<Location[]> {
  const params: { status?: number; currentDate?: string; lang: string } = { lang: getLanguage() };
  if (status !== undefined) {
    params.status = status;
  }
  if (currentDate) {
    params.currentDate = currentDate;
  }
  const response = await apiClient.get<Location[]>('/locations', { params });
  return response.data;
}

/**
 * The Beds search, answered by the server: the beds in use whose name, description or plants' names (removed ones
 * included) contain the text (any case), in the list's order, each the same object the unsearched list carries.
 *
 * @param q - What was typed.
 * @returns The beds found.
 */
export async function searchBeds(q: string): Promise<Location[]> {
  const response = await apiClient.get<Location[]>('/locations', { params: { status: 1, q, lang: getLanguage() } });
  return response.data;
}

/**
 * The beds in use, by id and name only, in the full list's order (the Seed vault last): what a bed drop-down lists.
 * No plants, notes, photos, diary or share counts are read.
 *
 * @returns The beds' ids and names.
 */
export async function getBedNames(): Promise<BedName[]> {
  const response = await apiClient.get<BedName[]>('/locations', { params: { short: 1, lang: getLanguage() } });
  return response.data;
}

/**
 * Creates a new location.
 * Expects FormData containing location details and an optional 'photo' file.
 * @param data FormData object.
 * @returns A promise that resolves to the creation response.
 */
export async function createLocation(data: FormData): Promise<LocationMutationResponse> {
  const response = await apiClient.post<LocationMutationResponse>('/locations', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

/**
 * Updates an existing location.
 * Expects FormData containing location details and an optional 'photo' file.
 * @param locationId The ID of the location to update.
 * @param data FormData object.
 * @returns A promise that resolves to the update response.
 */
export async function updateLocation(locationId: number, data: FormData): Promise<LocationMutationResponse> {
  const response = await apiClient.put<LocationMutationResponse>(`/locations/${locationId}`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

/**
 * What Add bed and Edit bed ask for each kind of bed (D55): the server's
 * table, so the form asks only what applies ("if its a greenhouse dont ask
 * about covers"; owner, 2026-10-16).
 *
 * @param lang - "en" or "es": the language the choices' names are said in.
 * @returns The kinds, the answers, what each kind asks, and the choices' names.
 */
export async function getBedQuestions(lang: string): Promise<BedQuestions> {
  const response = await apiClient.get<BedQuestions>('/locations/bed-questions', { params: { lang } });
  return response.data;
}

/**
 * The retired locations: everything with status 0.
 *
 * @returns The archived locations.
 */
export async function getArchivedLocations(): Promise<Location[]> {
  return getLocations(0);
}

/**
 * Archives a location (0) or restores it (1). The server refuses to archive
 * one that still has plants growing in it.
 *
 * @param locationId - The location.
 * @param status - 0 to archive, 1 to restore.
 */
export async function setLocationStatus(locationId: number, status: 0 | 1): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.patch<{ success: boolean; message: string }>(`/locations/${locationId}/status`, { status });
  return response.data;
}

/**
 * Deletes a location.
 * @param locationId The ID of the location to delete.
 * @returns A promise that resolves to the deletion response.
 */
export async function deleteLocation(locationId: number): Promise<LocationDeleteResponse> {
  const response = await apiClient.delete<LocationDeleteResponse>(`/locations/${locationId}`);
  return response.data;
}

/**
 * Takes the ticked plants in a location out of the garden (0), or brings them
 * back (1, for Undo). The location stays. Answers the plants that changed.
 *
 * @param locationId - The location.
 * @param plantObjIds - The ticked plants.
 * @param status - 0 to take them out, 1 to bring them back.
 */
export async function setLocationPlantsStatus(locationId: number, plantObjIds: number[], status: 0 | 1): Promise<number[]> {
  const response = await apiClient.post<{ success: boolean; plantObjIds: number[] }>(
    `/locations/${locationId}/plants/status`, { plantObjIds, status });
  return response.data.plantObjIds;
}

/**
 * One note on every ticked plant growing in a location. Answers the notes made.
 *
 * @param locationId - The location.
 * @param content - The note's words.
 * @param plantObjIds - The ticked plants.
 */
export async function addLocationNote(
  locationId: number, content: string, plantObjIds: number[],
): Promise<Array<{ plantObjId: number; noteId: number | null }>> {
  const response = await apiClient.post<{ success: boolean; notes: Array<{ plantObjId: number; noteId: number | null }> }>(
    `/locations/${locationId}/notes`, { content, plantObjIds });
  return response.data.notes;
}

/**
 * Undo for addLocationNote: takes those notes back off.
 *
 * @param locationId - The location.
 * @param noteIds - The notes addLocationNote made.
 */
export async function undoLocationNote(locationId: number, noteIds: number[]): Promise<void> {
  await apiClient.post(`/locations/${locationId}/notes/undo`, { noteIds });
}
